import { useEffect, useState } from "react";
import { apiGet } from "./api";
import { feesData as staticFeesData } from "./utils.statiquedata";

/** Détails de frais dont la somme compose le TOTAL de la ligne. */
export const FEE_DETAIL_KEYS: (keyof DetailsFrais)[] = [
  "frais_academiques",
  "releve",
  "reinscription",
  "carte",
  "bibliotheque",
  "coordination",
  "diplome",
  "construction",
  "stage",
  "jury",
];

export const FEE_DETAIL_LABELS: { key: keyof DetailsFrais; label: string; short: string }[] = [
  { key: "frais_academiques", label: "Frais académiques", short: "Frais Acad." },
  { key: "releve", label: "Relevé", short: "Relevé" },
  { key: "reinscription", label: "Réinscription", short: "Réinscr." },
  { key: "carte", label: "Carte", short: "Carte" },
  { key: "bibliotheque", label: "Bibliothèque", short: "Biblio." },
  { key: "coordination", label: "Coordination", short: "Coord." },
  { key: "diplome", label: "Diplôme", short: "Diplôme" },
  { key: "construction", label: "Construction", short: "Construc." },
  { key: "stage", label: "Stage", short: "Stage" },
  { key: "jury", label: "Jury", short: "Jury" },
];

export const FEE_TRANCHE_KEYS: (keyof Tranches)[] = ["premiere", "deuxieme", "troisieme"];

export const FEE_TRANCHE_LABELS: { key: keyof Tranches; label: string }[] = [
  { key: "premiere", label: "1ère tranche" },
  { key: "deuxieme", label: "2ème tranche" },
  { key: "troisieme", label: "3ème tranche" },
];

export const toFeeNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * TOTAL d'une ligne de frais = somme de tous les frais détaillés.
 * Une valeur nulle (frais non applicable) compte pour 0.
 */
export const computeFeeTotal = (
  details?: Partial<Record<keyof DetailsFrais, number | null>> | null
): number =>
  FEE_DETAIL_KEYS.reduce((sum, key) => {
    const value = toFeeNumber((details || {})[key]);
    return sum + (value === null ? 0 : value);
  }, 0);

/** Normalise une ligne (API ou statique) et recalcule son total. */
export const normalizeFeeRow = (row: any, index = 0): ItemFrais => {
  const rawDetails = (row?.details_frais || row?.detailsFrais || {}) as Record<string, any>;
  const rawTranches = (row?.tranches || {}) as Record<string, any>;

  const details_frais = {} as DetailsFrais;
  FEE_DETAIL_KEYS.forEach((key) => {
    (details_frais as any)[key] = toFeeNumber(rawDetails[key]);
  });

  const tranches = {} as Tranches;
  FEE_TRANCHE_KEYS.forEach((key) => {
    (tranches as any)[key] = toFeeNumber(rawTranches[key]);
  });

  const id = row?.id ?? undefined;

  return {
    key: Number(row?.key) || Number(id) || index + 1,
    ...(id ? { id } : {}),
    promotion: String(row?.promotion ?? ""),
    faculte: String(row?.faculte ?? ""),
    details_frais,
    total: computeFeeTotal(details_frais),
    tranches,
  } as ItemFrais;
};

export const normalizeFeeRows = (rows: any[]): ItemFrais[] =>
  (rows || []).map((row, index) => normalizeFeeRow(row, index));

/** Grille statique de secours (utilisée si l'API ne répond pas encore). */
export const fallbackFees = (): ItemFrais[] => normalizeFeeRows(staticFeesData as any[]);

type UseFeesResult = {
  fees: ItemFrais[];
  loading: boolean;
  source: "api" | "fallback";
};

/**
 * Charge la grille des frais depuis l'API (backoffice éditable).
 * Si l'API est indisponible ou vide, la grille statique est utilisée.
 */
export const useFees = (): UseFeesResult => {
  const [fees, setFees] = useState<ItemFrais[]>([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<"api" | "fallback">("fallback");

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const res = await apiGet("/fees");
        const rows = Array.isArray(res?.rows) ? res.rows : [];
        if (!active) return;
        if (rows.length) {
          setFees(normalizeFeeRows(rows));
          setSource("api");
        } else {
          setFees(fallbackFees());
          setSource("fallback");
        }
      } catch (e) {
        if (!active) return;
        setFees(fallbackFees());
        setSource("fallback");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return { fees, loading, source };
};
